import { Component } from "@theme/component";
import { trapFocus, removeTrapFocus } from "@theme/focus";
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from "@theme/utilities";

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ["details"];
  #mobileDrawerNavController = null;
  #mobileDrawerNavEnsureAttempts = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("keyup", this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#ensureMobileDrawerNavController({ allowRetry: true });
    queueMicrotask(() => this.#ensureMobileDrawerNavController({ allowRetry: true }));
  }

  disconnectedCallback() {
    this.#mobileDrawerNavController?.disconnect();
    this.#mobileDrawerNavController = null;
    this.#mobileDrawerNavEnsureAttempts = 0;
    super.disconnectedCallback();
    this.removeEventListener("keyup", this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== "Escape") return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute("open");
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest("details") ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    this.#ensureMobileDrawerNavController({ allowRetry: true });

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector("summary");

    if (!summary) return;

    details.open = true;
    summary.setAttribute("aria-expanded", "true");
    this.#updateExternalTrigger(true);

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add("menu-open");

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector(".menu-drawer, .menu-drawer__submenu");
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  #ensureMobileDrawerNavController({ allowRetry = false } = {}) {
    if (this.#mobileDrawerNavController) {
      this.#mobileDrawerNavEnsureAttempts = 0;
      return true;
    }

    const controller = createMobileDrawerNavController(this);
    if (!controller) {
      if (allowRetry && this.#mobileDrawerNavEnsureAttempts < 8) {
        this.#mobileDrawerNavEnsureAttempts += 1;
        requestAnimationFrame(() => this.#ensureMobileDrawerNavController({ allowRetry: true }));
      }
      return false;
    }

    controller.connect();
    this.#mobileDrawerNavController = controller;
    this.#mobileDrawerNavEnsureAttempts = 0;
    return true;
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector("summary");

    if (!summary) return;

    summary.setAttribute("aria-expanded", "false");
    details.classList.remove("menu-open");

    if (details === this.refs.details) {
      this.#updateExternalTrigger(false);
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector(".menu-drawer, .menu-drawer__submenu");

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll("details[open]:not(accordion-custom > details)");
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Sync aria-expanded on the external hamburger trigger (outside this component's DOM).
   * @param {boolean} isOpen
   */
  #updateExternalTrigger(isOpen) {
    const trigger = document.querySelector('[aria-controls="Details-menu-drawer-container"]');
    if (trigger) {
      trigger.setAttribute("aria-expanded", String(isOpen));
      const labelOpen = trigger.getAttribute("data-aria-label-menu");
      const labelClose = trigger.getAttribute("data-aria-label-close-menu");
      if (labelOpen && labelClose) {
        trigger.setAttribute("aria-label", isOpen ? labelClose : labelOpen);
      }
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll(".menu-drawer__animated-element");
    allAnimated.forEach((element) => {
      element.addEventListener("animationend", removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll("accordion-custom .details-content");

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add("details-content--no-animation");
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove("details-content--no-animation");
        }
      });
    }, 100);
  }
}

/**
 * @param {ParentNode} root
 */
function createMobileDrawerNavController(root) {
  const nav = root.querySelector("nav-menu[data-mobile-drawer-nav]");
  if (!(nav instanceof HTMLElement)) return null;

  return new MobileDrawerNavController(nav);
}

class MobileDrawerNavController {
  /** @type {number} */
  #lastPointerToggleAt = 0;
  /** @type {Element | null} */
  #lastPointerToggle = null;
  /** @type {number} */
  #lastPointerBackAt = 0;
  /** @type {number} */
  #lastHandledPointerAt = 0;
  /** @type {Element | null} */
  #lastHandledPointerTarget = null;

  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    this.activeTrigger = null;
    this.activePanel = null;
    this.activeLevel = 0;
    this.tabbedItems = null;
    this.details = root.closest("details");
    this.drawer = root.closest(".menu-drawer");
    this.supportsPointerEvents = typeof PointerEvent !== "undefined";

    this.onClick = this.onClick.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onDetailsToggle = this.onDetailsToggle.bind(this);
  }

  connect() {
    this.root.addEventListener("click", this.onClick, true);
    if (this.supportsPointerEvents) {
      this.root.addEventListener("pointerup", this.onPointerUp, true);
    }

    if (this.details instanceof HTMLDetailsElement) {
      this.details.addEventListener("toggle", this.onDetailsToggle);
    }
  }

  disconnect() {
    this.root.removeEventListener("click", this.onClick, true);
    if (this.supportsPointerEvents) {
      this.root.removeEventListener("pointerup", this.onPointerUp, true);
    }

    if (this.details instanceof HTMLDetailsElement) {
      this.details.removeEventListener("toggle", this.onDetailsToggle);
    }
  }

  onDetailsToggle() {
    if (this.details instanceof HTMLDetailsElement && !this.details.open) {
      this.closeToRoot();
    }
  }

  /**
   * iOS can be flaky about delivering a reliable `click` for some fixed/transformed UI.
   * We still listen to `click` for accessibility + mouse, but activate on `pointerup` for touch/pen.
   *
   * @param {PointerEvent} event
   */
  onPointerUp(event) {
    if (!(event instanceof PointerEvent)) return;
    if (event.pointerType !== "touch" && event.pointerType !== "pen") return;
    if (event.button !== 0) return;

    let target = null;
    if (event.target instanceof Element) {
      target = event.target;
    } else if (event.target instanceof Node) {
      target = event.target.parentElement;
    }
    if (!(target instanceof Element)) return;

    const back = target.closest("[data-mobile-drawer-back]");
    if (back && this.root.contains(back)) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      this.#lastPointerBackAt = performance.now();
      this.#markHandledPointer(back);
      this.handleBack();
      return;
    }

    const placeholderAnchor = this.#closestSuppressedNavigationAnchor(target);
    if (placeholderAnchor) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      this.#markHandledPointer(placeholderAnchor);
      return;
    }

    const trigger = target.closest("[data-mobile-toggle]");
    if (!trigger || !this.root.contains(trigger)) return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === "function") {
      event.stopImmediatePropagation();
    }

    this.#lastPointerToggleAt = performance.now();
    this.#lastPointerToggle = trigger;
    this.#markHandledPointer(trigger);

    const navItem = trigger.closest(".nav-menu__item");
    if (!(navItem instanceof HTMLElement)) return;
    const panelId = trigger.getAttribute("data-mobile-panel-id");
    if (!panelId) return;

    const level = Number.parseInt(trigger.getAttribute("data-mobile-level") || "1", 10);

    if (this.activeTrigger === trigger) {
      return;
    }

    /* Tabbed root: detached flyouts omit #mobile-panel-*; open tab strip + first tab panel only */
    if (navItem.dataset.menuType === "tabbed" && !navItem.hasAttribute("data-tab-of")) {
      this.openTabbedMenu(navItem);
      return;
    }

    const panel = this.root.querySelector(`#${CSS.escape(panelId)}`);
    if (!(panel instanceof HTMLElement)) return;

    if (!navItem.hasAttribute("data-tab-of")) {
      this.hideTabbedItems();
    }

    this.openItem(trigger, panel, navItem, level);
  }

  /**
   * @param {Event} event
   */
  onClick(event) {
    let target = null;
    if (event.target instanceof Element) {
      target = event.target;
    } else if (event.target instanceof Node) {
      target = event.target.parentElement;
    }
    if (!(target instanceof Element)) return;

    if (this.#shouldIgnorePotentialGhostClick(target)) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      return;
    }

    const back = target.closest("[data-mobile-drawer-back]");
    if (back && this.root.contains(back)) {
      if (this.#shouldIgnoreFollowUpClickAfterPointerBack()) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      event.preventDefault();
      this.handleBack();
      return;
    }

    const placeholderAnchor = this.#closestSuppressedNavigationAnchor(target);
    if (placeholderAnchor) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      return;
    }

    const trigger = target.closest("[data-mobile-toggle]");
    if (!trigger || !this.root.contains(trigger)) return;

    if (this.#shouldIgnoreFollowUpClickAfterPointerToggle(trigger)) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === "function") {
      event.stopImmediatePropagation();
    }

    const navItem = trigger.closest(".nav-menu__item");
    if (!(navItem instanceof HTMLElement)) return;
    const panelId = trigger.getAttribute("data-mobile-panel-id");
    if (!panelId) return;

    const level = Number.parseInt(trigger.getAttribute("data-mobile-level") || "1", 10);

    if (this.activeTrigger === trigger) {
      // Keep panel open on repeated taps; close via back button only.
      return;
    }

    if (navItem.dataset.menuType === "tabbed" && !navItem.hasAttribute("data-tab-of")) {
      this.openTabbedMenu(navItem);
      return;
    }

    const panel = this.root.querySelector(`#${CSS.escape(panelId)}`);
    if (!(panel instanceof HTMLElement)) return;

    if (!navItem.hasAttribute("data-tab-of")) {
      this.hideTabbedItems();
    }

    this.openItem(trigger, panel, navItem, level);
  }

  /**
   * @returns {boolean}
   */
  #shouldIgnoreFollowUpClickAfterPointerBack() {
    return performance.now() - this.#lastPointerBackAt < 800;
  }

  /**
   * @param {Element} trigger
   * @returns {boolean}
   */
  #shouldIgnoreFollowUpClickAfterPointerToggle(trigger) {
    return trigger === this.#lastPointerToggle && performance.now() - this.#lastPointerToggleAt < 800;
  }

  /**
   * @param {Element} target
   * @returns {boolean}
   */
  #shouldIgnorePotentialGhostClick(target) {
    if (performance.now() - this.#lastHandledPointerAt >= 700) return false;
    const anchor = target.closest("a[href]");
    if (!(anchor instanceof HTMLAnchorElement) || !this.root.contains(anchor)) return false;
    const handledTarget = this.#lastHandledPointerTarget;
    return !(handledTarget instanceof Element && (anchor === handledTarget || anchor.contains(handledTarget)));
  }

  /**
   * @param {Element} target
   */
  #markHandledPointer(target) {
    this.#lastHandledPointerAt = performance.now();
    this.#lastHandledPointerTarget = target;
  }

  /**
   * Anchors that must never activate in-app / browser navigation (placeholders, scripted hrefs, link-styled controls).
   *
   * @param {Element} target
   * @returns {HTMLAnchorElement | null}
   */
  #closestSuppressedNavigationAnchor(target) {
    if (!(target instanceof Element)) return null;
    const anchor = target.closest("a[href]");
    if (!(anchor instanceof HTMLAnchorElement) || !this.root.contains(anchor)) return null;
    if (anchor.closest("[data-mobile-toggle], [data-nav-row-toggle]")) return null;
    if (anchor.getAttribute("role") === "button") return anchor;

    const href = (anchor.getAttribute("href") ?? "").trim();
    if (href === "" || href === "#" || href.toLowerCase().startsWith("javascript:")) {
      return anchor;
    }

    return null;
  }

  handleBack() {
    if (this.activeLevel > 1) {
      this.closeActiveSubmenu();
      this.hideTabbedItems();
      return;
    }

    if (this.tabbedItems) {
      this.closeActiveSubmenu();
      this.hideTabbedItems();
      return;
    }

    this.closeActiveSubmenu();
  }

  /**
   * @param {Element} trigger
   * @param {HTMLElement} panel
   * @param {HTMLElement} navItem
   * @param {number} level
   */
  openItem(trigger, panel, navItem, level = 1) {
    if (this.activeTrigger instanceof Element) {
      this.activeTrigger.setAttribute("aria-expanded", "false");
    }

    if (this.activePanel instanceof HTMLElement) {
      this.activePanel.removeAttribute("data-mobile-panel-active");
    }

    this.activeTrigger = trigger;
    this.activePanel = panel;
    this.activeLevel = level;
    trigger.setAttribute("aria-expanded", "true");
    panel.setAttribute("data-mobile-panel-active", "");

    this.root.setAttribute("data-submenu-open", "");
    this.root.setAttribute("data-active-menu-type", navItem.getAttribute("data-menu-type") || "default");

    if (this.drawer instanceof HTMLElement) {
      this.drawer.setAttribute("data-submenu-open", "");
    }

    this.syncDrawerChromeTop();
  }

  closeActiveSubmenu() {
    if (!(this.activeTrigger instanceof Element)) {
      this.root.removeAttribute("data-submenu-open");
      this.root.removeAttribute("data-active-menu-type");

      if (this.drawer instanceof HTMLElement) {
        this.drawer.removeAttribute("data-submenu-open");
      }
      this.activePanel = null;
      this.activeLevel = 0;
      return;
    }

    this.activeTrigger.setAttribute("aria-expanded", "false");
    this.activeTrigger = null;
    if (this.activePanel instanceof HTMLElement) {
      this.activePanel.removeAttribute("data-mobile-panel-active");
    }
    this.activePanel = null;
    this.activeLevel = 0;

    this.root.removeAttribute("data-submenu-open");
    this.root.removeAttribute("data-active-menu-type");

    if (this.drawer instanceof HTMLElement) {
      this.drawer.removeAttribute("data-submenu-open");
    }
  }

  closeToRoot() {
    this.closeActiveSubmenu();
    this.hideTabbedItems();
  }

  /**
   * @param {HTMLElement} navItem
   */
  openTabbedMenu(navItem) {
    this.closeActiveSubmenu();

    const blockId = navItem.dataset.blockId;
    const selector = blockId ? `.nav-menu__item[data-tab-of="${CSS.escape(blockId)}"]` : ".nav-menu__item[data-tab-of]";
    const tabItems = Array.from(this.root.querySelectorAll(selector)).filter((item) => item instanceof HTMLElement);
    if (tabItems.length === 0) return;

    this.tabbedItems = tabItems;

    for (const item of this.root.querySelectorAll(".nav-menu__item:not(.nav-menu__item--tab)")) {
      if (item instanceof HTMLElement) item.hidden = true;
    }

    for (const tab of tabItems) {
      tab.hidden = false;
    }

    const firstToggle = tabItems
      .find((item) => item.querySelector("[data-mobile-toggle]"))
      ?.querySelector("[data-mobile-toggle]");
    if (firstToggle instanceof HTMLElement) {
      const firstPanelId = firstToggle.getAttribute("data-mobile-panel-id");
      const firstPanel = firstPanelId ? this.root.querySelector(`#${CSS.escape(firstPanelId)}`) : null;
      const firstItem = firstToggle.closest(".nav-menu__item");
      const level = Number.parseInt(firstToggle.getAttribute("data-mobile-level") || "2", 10);
      if (firstPanel instanceof HTMLElement && firstItem instanceof HTMLElement) {
        this.openItem(firstToggle, firstPanel, firstItem, level);
      }
    }
  }

  hideTabbedItems() {
    if (!this.tabbedItems) return;

    for (const tab of this.tabbedItems) {
      tab.hidden = true;
    }

    for (const item of this.root.querySelectorAll(".nav-menu__item:not(.nav-menu__item--tab)")) {
      if (item instanceof HTMLElement) item.hidden = false;
    }

    this.tabbedItems = null;
  }

  syncDrawerChromeTop() {
    if (!(this.drawer instanceof HTMLElement)) return;

    const tabBar = this.root.querySelector(".nav-menu-drawer__tab-bar");
    if (!(tabBar instanceof HTMLElement)) return;

    const chromeTop = Math.round(tabBar.getBoundingClientRect().bottom - this.drawer.getBoundingClientRect().top);
    this.drawer.style.setProperty("--nav-drawer-chrome-top", `${chromeTop}px`);
  }
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove("menu-open");
  element.removeAttribute("open");
  element.querySelector("summary")?.setAttribute("aria-expanded", "false");
}

if (!customElements.get("header-drawer")) {
  customElements.define("header-drawer", HeaderDrawer);
}
